import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateTicketCustomFields } from 'api/tickets';
import * as CustomFieldResponseForm from 'components/custom-fields/CustomFieldResponseForm';
import * as CFHelpers from 'components/custom-fields/helpers';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { CombinedField } from 'customHooks/useCombinedFields';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Ticket } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface CustomFieldForm {
    customFields: { [field: string]: CFHelpers.CustomFieldFormValue };
}

interface EditableCustomFieldProps {
    field: CombinedField;
    ticket: Ticket;
}

const TicketEditableCustomField = (props: EditableCustomFieldProps) => {
    const [isAddingTicketFile, setIsAddingTicketFile] = useState(false);
    const { ticket, field } = props;
    const { success, error } = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const { key, forceRender } = useForceRender();

    const methods = Form.useGivebutterForm<CustomFieldForm>({
        defaultValues: {
            customFields: CFHelpers.toFormValues([field]),
        },
    });

    const { handleSubmit, getValues } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: () => {
            const value = CFHelpers.toRequestValues(getValues('customFields'), [field], {
                useId: true,
            });
            return updateTicketCustomFields(ticket?.id, { custom_fields: value });
        },
        onSuccess: () => {
            success('Successfully updated custom fields!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.ticketById(accountId, ticket?.id),
            });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to update custom fields');
        },
    });

    return (
        <EditableInput.Root key={key}>
            <Form.Provider {...methods}>
                <CustomFieldResponseForm.Root field={field} onAddFile={setIsAddingTicketFile}>
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col">
                            <CustomFieldResponseForm.Title />
                            <CustomFieldResponseForm.Description />
                        </div>
                        <div>
                            <EditableInput.ReadOnly>
                                <CustomFieldResponseForm.ReadOnlyInput />
                                <EditableInput.ReadOnlyEditTrigger />
                            </EditableInput.ReadOnly>
                            <EditableInput.Content>
                                <form onSubmit={handleSubmit(() => submit())}>
                                    <Form.Container className="flex flex-col gap-4">
                                        <CustomFieldResponseForm.Input />
                                        <CustomFieldResponseForm.FormError />
                                        <EditableInput.ActionsContainer>
                                            <EditableInput.Cancel />
                                            <Form.SubmitButton disabled={isAddingTicketFile}>
                                                Save
                                            </Form.SubmitButton>
                                        </EditableInput.ActionsContainer>
                                    </Form.Container>
                                </form>
                            </EditableInput.Content>
                        </div>
                    </div>
                </CustomFieldResponseForm.Root>
            </Form.Provider>
        </EditableInput.Root>
    );
};

export { TicketEditableCustomField };
